import {
  CUSTOM_WORKFLOW_TYPE_LABEL,
  CustomOperatorValue,
  getDateRangeFromChartBucketKey,
  getIsWorkflowChartDimension,
  WORKFLOW_CHART_DIMENSIONS,
  WORKFLOW_CHART_EMPTY_BUCKET_KEYS,
  WORKFLOW_CHART_HIDDEN_SERIES_KEY,
} from '@aline/common';
import type { WorkflowChartDimension } from '@aline/common';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-premium';
import { z } from 'zod';

import type { InsightChartModel } from './insight-chart-series';

export const WORKFLOW_DRILLDOWN_FILTER_ID_PREFIX = 'insight-drilldown-';

export type WorkflowDrilldownBucket = {
  dimension: WorkflowChartDimension;
  key: string;
  label: string;
};

/** A clicked bar, slice or point: which x bucket, and which stacked series if any. */
export type InsightChartDataPoint = {
  dataIndex: number;
  seriesId?: string;
};

export type WorkflowDrilldownPayload = {
  buckets: WorkflowDrilldownBucket[];
  /** System Chart Preset id when drilling from a System Chart. */
  presetId?: string;
  /** Persistable Workflow View state when drilling from an Insight Pin. */
  viewState?: unknown;
};

const WorkflowDrilldownBucketSchema = z.object({
  dimension: z.string().refine(getIsWorkflowChartDimension),
  key: z.string(),
  label: z.string(),
});

const WorkflowDrilldownPayloadSchema = z.object({
  buckets: z.array(WorkflowDrilldownBucketSchema).max(2),
  presetId: z.string().optional(),
  viewState: z.unknown().optional(),
});

export function parseWorkflowDrilldownPayload(value: string): WorkflowDrilldownPayload | null {
  try {
    const parsed = WorkflowDrilldownPayloadSchema.safeParse(JSON.parse(value));
    if (!parsed.success) return null;
    const buckets: WorkflowDrilldownBucket[] = [];
    for (const bucket of parsed.data.buckets) {
      if (!getIsWorkflowChartDimension(bucket.dimension)) return null;
      buckets.push({
        dimension: bucket.dimension,
        key: bucket.key,
        label: bucket.label,
      });
    }
    return {
      buckets,
      presetId: parsed.data.presetId,
      viewState: parsed.data.viewState,
    };
  } catch {
    return null;
  }
}

export function serializeWorkflowDrilldownPayload(payload: WorkflowDrilldownPayload): string {
  return JSON.stringify(payload);
}

/** Maps a clicked Chart point onto the Dimension buckets the Workflows list should filter by. */
export function getDrilldownBucketsFromChartPoint(options: {
  dimensions: readonly string[];
  model: InsightChartModel;
  point: InsightChartDataPoint;
  valueDateDimensions?: Partial<Record<string, WorkflowChartDimension>>;
}): WorkflowDrilldownBucket[] {
  const { dimensions, model, point, valueDateDimensions } = options;

  const seriesDateDimension = point.seriesId ? valueDateDimensions?.[point.seriesId] : undefined;
  const primaryDimension =
    seriesDateDimension && getIsWorkflowChartDimension(seriesDateDimension)
      ? seriesDateDimension
      : dimensions.at(0);
  if (!primaryDimension || !getIsWorkflowChartDimension(primaryDimension)) return [];

  const key = model.xKeys[point.dataIndex];
  const label = model.xLabels[point.dataIndex];
  if (key === undefined || label === undefined) return [];

  /**
   * Other stands for many buckets at once, whether it is the remainder series or
   * a remainder tick on the category axis.
   */
  if (key === WORKFLOW_CHART_HIDDEN_SERIES_KEY) return [];
  if (point.seriesId === WORKFLOW_CHART_HIDDEN_SERIES_KEY) return [];

  const buckets: WorkflowDrilldownBucket[] = [{ dimension: primaryDimension, key, label }];

  const secondaryDimension = dimensions.at(1);
  if (
    !seriesDateDimension &&
    secondaryDimension &&
    getIsWorkflowChartDimension(secondaryDimension) &&
    point.seriesId
  ) {
    const series = model.series.find(item => item.id === point.seriesId);
    if (series) {
      buckets.push({
        dimension: secondaryDimension,
        key: point.seriesId,
        label: series.label,
      });
    }
  }

  return buckets;
}

/**
 * Build DataGrid filter items for Chart Dimension buckets.
 * Date Dimensions expand to onOrAfter + onOrBefore (or isEmpty).
 */
export function getFilterItemsFromDrilldownBuckets(
  buckets: WorkflowDrilldownBucket[]
): GridFilterItem[] {
  return buckets.flatMap((bucket, index) => getFilterItemsForDrilldownBucket(bucket, index));
}

export function getIsDrilldownFilterItemId(id: string | number | undefined): boolean {
  return typeof id === 'string' && id.startsWith(WORKFLOW_DRILLDOWN_FILTER_ID_PREFIX);
}

export function omitDrilldownFilterItems(items: GridFilterItem[]): GridFilterItem[] {
  return items.filter(item => !getIsDrilldownFilterItemId(item.id));
}

export function composeFilterModelWithDrilldown(options: {
  buckets: WorkflowDrilldownBucket[];
  filterModel: GridFilterModel;
}): GridFilterModel {
  const baseItems = omitDrilldownFilterItems(options.filterModel.items ?? []);
  const drilldownItems = getFilterItemsFromDrilldownBuckets(options.buckets);
  return {
    ...options.filterModel,
    items: [...baseItems, ...drilldownItems],
    logicOperator: GridLogicOperator.And,
  };
}

function getFilterItemsForDrilldownBucket(
  bucket: WorkflowDrilldownBucket,
  index: number
): GridFilterItem[] {
  const id = `${WORKFLOW_DRILLDOWN_FILTER_ID_PREFIX}${index}`;
  const { dimension, key, label } = bucket;

  switch (dimension) {
    case WORKFLOW_CHART_DIMENSIONS.assignee: {
      if (key === WORKFLOW_CHART_EMPTY_BUCKET_KEYS.assignee) {
        return [
          {
            field: WORKFLOW_CHART_DIMENSIONS.assignee,
            id,
            operator: CustomOperatorValue.WorkflowAssigneeIsEmpty,
            value: [],
          },
        ];
      }
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.assignee,
          id,
          operator: CustomOperatorValue.WorkflowAssigneeAnyOf,
          value: [{ __typename: 'User', displayName: label, email: '', id: key }],
        },
      ];
    }
    case WORKFLOW_CHART_DIMENSIONS.board:
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.board,
          id,
          operator: CustomOperatorValue.WorkflowBoard,
          value: [{ __typename: 'Board', id: key, name: label }],
        },
      ];
    case WORKFLOW_CHART_DIMENSIONS.column:
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.column,
          id,
          operator: CustomOperatorValue.WorkflowColumn,
          value: [{ __typename: 'Column', id: key, name: label }],
        },
      ];
    case WORKFLOW_CHART_DIMENSIONS.columnCategory: {
      if (key === WORKFLOW_CHART_EMPTY_BUCKET_KEYS.columnCategory) {
        return [
          {
            field: WORKFLOW_CHART_DIMENSIONS.columnCategory,
            id,
            operator: CustomOperatorValue.WorkflowColumnCategoryIsEmpty,
            value: [],
          },
        ];
      }
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.columnCategory,
          id,
          operator: 'isAnyOf',
          value: [key],
        },
      ];
    }
    case WORKFLOW_CHART_DIMENSIONS.owner:
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.owner,
          id,
          operator: CustomOperatorValue.Owner,
          value: [{ __typename: 'User', displayName: label, email: '', id: key }],
        },
      ];
    case WORKFLOW_CHART_DIMENSIONS.priority: {
      if (key === WORKFLOW_CHART_EMPTY_BUCKET_KEYS.priority) {
        return [
          {
            field: WORKFLOW_CHART_DIMENSIONS.priority,
            id,
            operator: CustomOperatorValue.WorkflowPriorityIsEmpty,
            value: [],
          },
        ];
      }
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.priority,
          id,
          operator: CustomOperatorValue.WorkflowPriority,
          value: [key],
        },
      ];
    }
    case WORKFLOW_CHART_DIMENSIONS.workflowType: {
      if (key === WORKFLOW_CHART_EMPTY_BUCKET_KEYS.workflowType) {
        return [
          {
            field: WORKFLOW_CHART_DIMENSIONS.workflowType,
            id,
            operator: CustomOperatorValue.WorkflowType,
            value: [
              {
                __typename: 'CustomWorkflowType',
                id: 'custom',
                label: CUSTOM_WORKFLOW_TYPE_LABEL,
              },
            ],
          },
        ];
      }
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.workflowType,
          id,
          operator: CustomOperatorValue.WorkflowType,
          value: [{ __typename: 'WorkflowTypeOption', id: key, label }],
        },
      ];
    }
    case WORKFLOW_CHART_DIMENSIONS.completedAt:
    case WORKFLOW_CHART_DIMENSIONS.createdAt:
    case WORKFLOW_CHART_DIMENSIONS.signedAt:
    case WORKFLOW_CHART_DIMENSIONS.startedAt:
      return getDateFilterItemsForDrilldownBucket(dimension, key, id);
    case WORKFLOW_CHART_DIMENSIONS.state: {
      if (key === WORKFLOW_CHART_EMPTY_BUCKET_KEYS.state) {
        return [
          {
            field: WORKFLOW_CHART_DIMENSIONS.state,
            id,
            operator: CustomOperatorValue.WorkflowStateIsEmpty,
            value: [],
          },
        ];
      }
      return [
        {
          field: WORKFLOW_CHART_DIMENSIONS.state,
          id,
          operator: CustomOperatorValue.WorkflowState,
          value: [key],
        },
      ];
    }
    default: {
      const _exhaustive: never = dimension;
      return _exhaustive;
    }
  }
}

function getDateFilterItemsForDrilldownBucket(
  field:
    | typeof WORKFLOW_CHART_DIMENSIONS.createdAt
    | typeof WORKFLOW_CHART_DIMENSIONS.completedAt
    | typeof WORKFLOW_CHART_DIMENSIONS.signedAt
    | typeof WORKFLOW_CHART_DIMENSIONS.startedAt,
  key: string,
  idPrefix: string
): GridFilterItem[] {
  const range = getDateRangeFromChartBucketKey(key);
  if (!range) return [];
  if (range.kind === 'empty') {
    return [{ field, id: idPrefix, operator: 'isEmpty', value: undefined }];
  }
  return [
    { field, id: `${idPrefix}-start`, operator: 'onOrAfter', value: range.start },
    { field, id: `${idPrefix}-end`, operator: 'onOrBefore', value: range.end },
  ];
}
